import type { BaseQuestion, Extents, GetAnswerFunc, LandingPageObject, QuestionObject, SetAnswerFunc } from 'landing_pages/types'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import Question from '../../components/common/Question'
import useEvent from 'landing_pages/common/use-event'
import { stripHTML } from '../../../lib/utils'

import PreactButton from 'landing_pages/components/common/PreactButton'
import IconFlowError from 'landing_pages/components/UI/IconFlowError'
import { getVisibleQuestions } from 'landing_pages/common/get-visible-questions'
import { validate } from 'landing_pages/common/validate'
import PreactCheckbox from 'landing_pages/components/UI/PreactCheckbox'
import { trackQuestionCompleted, trackQuestionErrored } from 'landing_pages/tracking'
import AgataQuestion from '../../components/UI/AgataQuestion'
import { InnerHTMLHelper } from 'landing_pages/common/utils'

function AgataProgressBar({percentComplete}) {
  const fillStyle = {
    width: `${percentComplete}%`,
  }

  return (
    <div className='progress-bar-container' >
      <div className='progress-bar' style={fillStyle} />
      {/* showProgressBarLabel was tested with Google Optimize but is not currently supported */}
    </div>
  )
}

export function getTcpaDisclosureText(landingPage: LandingPageObject) {
  if(landingPage.isMultiClient) {
    return `I acknowledge that, by clicking the checkbox as my official signature, I consent to representatives of Cygnus Education contacting me about educational opportunities via email, text, or phone, including my mobile phone number(s) entered above, using an automatic dialer, or pre-recorded message. Message and data rates may apply. I understand that my consent is not a requirement for enrollment, and I may withdraw my consent at any time.`
  } else {
    if(landingPage.primaryClientTcpa) {
      return stripHTML(landingPage.primaryClientTcpa)
    }else if(landingPage.variables.tcpaDisclosure) {
      return stripHTML(landingPage.variables.tcpaDisclosure as string)
    } else {
      return `By submitting this form, I authorize and agree that a representative of ${landingPage.variables.schoolName} can contact me by phone and/or SMS text message at the telephone number provided by me. I understand that the call or text may include advertising or telemarketing messages using an automated telephone dialing system, or an artificial or pre-recorded voice or text message. Data and text message rates may apply. I understand that my agreement above is not required before I purchase any educational programs or services. Calls may be recorded for quality assurance purposes.`
    }
  }
}

function TcpaDisclosure({ landingPage }) {
  if(landingPage.isMultiClient) {
    return <>
      I acknowledge that, by clicking the checkbox as my official signature, I consent to representatives of <span data-tf-element-role="consent-advertiser-name">Cygnus Education</span> contacting me about educational opportunities via email, text, or phone, including my mobile phone number(s) entered above, using an automatic dialer, or pre-recorded message. Message and data rates may apply. I understand that my consent is not a requirement for enrollment, and I may withdraw my consent at any time.
    </>
  } else {
    if(landingPage.primaryClientTcpa) {
      return <span dangerouslySetInnerHTML={{__html: landingPage.primaryClientTcpa}}/>
    }
    else if(landingPage.variables.tcpaDisclosure) {
      return <span dangerouslySetInnerHTML={{__html: landingPage.variables.tcpaDisclosure}}/>
    } else {
      return <>
        By submitting this form, I authorize and agree that a representative of <span data-tf-element-role="consent-advertiser-name">{landingPage.variables.schoolName}</span> can contact me by phone and/or SMS text message at the telephone number provided by me. I understand that the call or text may include advertising or telemarketing messages using an automated telephone dialing system, or an artificial or pre-recorded voice or text message. Data and text message rates may apply. I understand that my agreement above is not required before I purchase any educational programs or services. Calls may be recorded for quality assurance purposes.
      </>
    }
  }
}

export default function Step({
  pending,
  landingPage,
  extents,
  currentStep,
  numSteps,
  getAnswer,
  setAnswer,
  initialAnswers,
  onCompleteStep,
} : {
  pending: boolean
  landingPage: LandingPageObject
  extents: Extents
  currentStep: number
  numSteps: number
  getAnswer: GetAnswerFunc
  setAnswer: SetAnswerFunc
  initialAnswers: Record<string, string>
  onCompleteStep: (extraQuestions?: BaseQuestion[]) => void
}) {
  const step = landingPage.steps[currentStep]
  const visibleQuestions = getVisibleQuestions(step.questions, getAnswer)

  const showTcpa = visibleQuestions.some(question => question.type === 'tel')

  const hasPreselectedAnswer = useMemo(() => visibleQuestions.some(question => typeof(initialAnswers[question.key]) === 'string' && initialAnswers[question.key] !== ''), [initialAnswers, visibleQuestions])
  const allowAutoAdvance = !landingPage.variables.disableAutoAdvance && !hasPreselectedAnswer

  const hasImplicitTcpa = showTcpa && landingPage.variables.implicitTcpa

  const [isValidating, setIsValidating] = useState<boolean>(false)
  const [errors, setErrors] = useState<Record<string,string>>({})
  const errorMessage = Object.values(errors).join("\r\n")
  const defaultSubmitButtonText = landingPage.isMultiClient ? 'VIEW RESULTS' : 'REQUEST FREE INFO'
  const submitButtonText = currentStep < numSteps -1 ? 'CONTINUE' : (landingPage.variables.submitButtonLabel || defaultSubmitButtonText)
  const cta = landingPage.isMultiClient ? landingPage.variables.cta as string : (extents['hl'] || landingPage.variables.cta as string)
  const tcpaKey = landingPage.isMultiClient ? 'SecondaryTCPA' : 'Agree'
  const showSecurityMessage = landingPage.variables.showSecurityMessage ?? true

  const validateGuard = useRef(false)

  const startValidation = useEvent((highlightErrors = true) => {
    if(validateGuard.current) {
      return
    }

    const currentVisibleQuestions = getVisibleQuestions(step.questions, getAnswer)

    validateGuard.current = true
    setIsValidating(true)
    setErrors({})
    let questionsToValidate: QuestionObject[]
    let tcpaQuestion: QuestionObject
    if(showTcpa && !hasImplicitTcpa) {
      tcpaQuestion = {
        type: 'checkbox',
        key: tcpaKey,
        text: 'checkbox to acknowledge consent',
      }
      questionsToValidate = [...currentVisibleQuestions, tcpaQuestion]
    } else {
      questionsToValidate = [...currentVisibleQuestions]
    }
    validate(questionsToValidate, getAnswer)
      .then(errors => {
        if(Object.keys(errors).length === 0) {
          onCompleteStep(tcpaQuestion?.key === 'SecondaryTCPA' ? [{ key: 'SecondaryTCPA', text: 'SecondaryTCPA' }] : [])
        }
        else if(highlightErrors) {
          setErrors(errors)
          if(Object.keys(errors).length) {
            Object.keys(errors).forEach(key => {
              const question = currentVisibleQuestions.find(q => q.key === key)
              trackQuestionErrored(question?.key, question?.text, errors[key], currentStep)
            })
          }
        }
      })
      .finally(() => {
        validateGuard.current = false
        setIsValidating(false)
      })
  })

  const tryAutoAdvance = useEvent(() => {
    if(allowAutoAdvance && currentStep < numSteps -1) {
      startValidation(false)
    }
  })

  const mapsAutoCompleteHandler = useEvent((props: Record<string,string>) => {
    for(const [key, value] of Object.entries(props)) {
      setAnswer(key, value)
    }
  })

  useEffect(() => {
    if (visibleQuestions.length === 0) {
      onCompleteStep()
    }
  }, [onCompleteStep, visibleQuestions.length])

  if (visibleQuestions.length === 0) {
    return null
  }

  const showLabels = !!landingPage.variables.showLabels

  return (
    <>
      {!!landingPage.isMultiClient && (
        <div>
          <h1>{landingPage.variables.heading as string}</h1>
          <h3 className="subheading">{(extents['hl'] || landingPage.variables.subheading) as string}</h3>
        </div>
      )}

      <div className={"steps"}>
        <AgataProgressBar percentComplete={100 * (currentStep + 1) / numSteps} />

        <div className="step">
          {currentStep === 0 && (
            <>
              {cta && (
                <h2><InnerHTMLHelper html={cta}/></h2>
              )}
              {landingPage.steps.length < 4 && (
                <h3>{landingPage.steps.length} short step{landingPage.steps.length > 1 ? "s" : ''}:</h3>
              )}
            </>
          ) || (
            <div className='vertical-space'></div>
          )}

          {landingPage.isMultiClient && (
            <h1>{step.title || 'Please select...'}</h1>
          )}

          <div className="questions" style={{ gap: showLabels ? '6px' : '18px' }}>
            {visibleQuestions.map((question, index) => (
              <div className="question" key={`${question.key}-${index}`}>
                <Question
                  question={question}
                  questionComponent={AgataQuestion}
                  value={getAnswer(question.key)}
                  setValue={(newValue: string) => { setAnswer(question.key, newValue) }}
                  isError={question.key in errors}
                  mapsAutoComplete={question.key === 'StreetAddress' ? mapsAutoCompleteHandler : undefined}
                  onCommit={() => tryAutoAdvance()}
                  currentStep={currentStep}
                  showLabels={showLabels}
                />
              </div>
            ))}

            {hasImplicitTcpa && (
              <label className="tcpa_disclosure tcpa_implicit">
                <input type="hidden" data-leadid-type="disclosure" />
                <TcpaDisclosure landingPage={landingPage}/>
              </label>
            ) || showTcpa && (
              <PreactCheckbox
                className="tcpa_disclosure"
                isError={tcpaKey in errors}
                isChecked={!!getAnswer(tcpaKey)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setAnswer(tcpaKey, event.currentTarget.checked ? '1' : '')
                  trackQuestionCompleted(tcpaKey, getTcpaDisclosureText(landingPage), currentStep)
                }}
                id="leadid_tcpa_disclosure"
                inputProps={{
                  "data-tf-element-role": "consent-opt-in",
                }}
                labelProps={{
                  "data-tf-element-role": "consent-language",
                }}
              >
                <TcpaDisclosure landingPage={landingPage}/>
              </PreactCheckbox>
            )}
          </div>

          <PreactButton className="continue-button" onClick={startValidation} label={submitButtonText as string} isDisabled={ isValidating || pending} isLoading={isValidating || pending} />
          {errorMessage &&
            <IconFlowError error={errorMessage} />
          }

          {showSecurityMessage && (
            <div className="security_message">
              <i className="fas fa-lock" aria-hidden={true} style={{marginRight: '3px'}}></i>
              Your information is always private and secure.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
